import { useState } from 'react';
import MainLayout from '../../../components/layout/MainLayout';
import SearchFilters, { type SearchFilters as Filters } from './components/SearchFilters';
import TenantTable from './components/TenantTable';
import TenantDetailModal from './components/TenantDetailModal';
import TenantFormModal from './components/TenantFormModal';
import ConfirmModal from './components/ConfirmModal';
import { mockTenants } from './mockData';
import type { Tenant, TenantFormData } from './types';

export default function TenantListPage() {
  const [tenants, setTenants] = useState<Tenant[]>(mockTenants);
  const [filteredTenants, setFilteredTenants] = useState<Tenant[]>(mockTenants);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [detailTenant, setDetailTenant] = useState<Tenant | null>(null);
  const [editTenant, setEditTenant] = useState<Tenant | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [batchAction, setBatchAction] = useState<{ show: boolean; action: 'enable' | 'disable' }>({
    show: false,
    action: 'disable'
  });

  const handleSearch = (filters: Filters) => {
    let filtered = [...tenants];

    if (filters.tenantId) {
      filtered = filtered.filter(t => t.id.includes(filters.tenantId));
    }

    if (filters.name) {
      filtered = filtered.filter(t => t.name.toLowerCase().includes(filters.name.toLowerCase()));
    }

    if (filters.status !== 'all') {
      filtered = filtered.filter(t => t.status === filters.status);
    }

    if (filters.type !== 'all') {
      filtered = filtered.filter(t => t.type === filters.type);
    }

    if (filters.startDate) {
      filtered = filtered.filter(t => t.createDate >= filters.startDate);
    }

    if (filters.endDate) {
      filtered = filtered.filter(t => t.createDate <= filters.endDate);
    }

    setFilteredTenants(filtered);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(filteredTenants.map(t => t.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter(sid => sid !== id));
    }
  };

  const handleStatusChange = (id: string, status: 'active' | 'disabled') => {
    const updatedTenants = tenants.map(t =>
      t.id === id ? { ...t, status } : t
    );
    setTenants(updatedTenants);
    setFilteredTenants(filteredTenants.map(t =>
      t.id === id ? { ...t, status } : t
    ));
    if (detailTenant?.id === id) {
      setDetailTenant({ ...detailTenant, status });
    }
  };

  const handleRemarkChange = (id: string, remark: string) => {
    const updatedTenants = tenants.map(t =>
      t.id === id ? { ...t, remark } : t
    );
    setTenants(updatedTenants);
    setFilteredTenants(filteredTenants.map(t =>
      t.id === id ? { ...t, remark } : t
    ));
  };

  const handleBatchAction = (action: 'enable' | 'disable') => {
    if (selectedIds.length === 0) {
      alert('请先选择要操作的租户');
      return;
    }
    setBatchAction({ show: true, action });
  };

  const handleConfirmBatchAction = () => {
    const newStatus = batchAction.action === 'enable' ? 'active' : 'disabled';
    const updatedTenants = tenants.map(t =>
      selectedIds.includes(t.id) ? { ...t, status: newStatus } : t
    );
    setTenants(updatedTenants);
    setFilteredTenants(filteredTenants.map(t =>
      selectedIds.includes(t.id) ? { ...t, status: newStatus } : t
    ));
    setSelectedIds([]);
    setBatchAction({ show: false, action: 'disable' });
  };

  const handleSave = (data: TenantFormData) => {
    if (editTenant) {
      const updatedTenants = tenants.map(t =>
        t.id === editTenant.id
          ? {
              ...t,
              ...data,
              updateDate: new Date().toISOString().replace('T', ' ').substring(0, 19)
            }
          : t
      );
      setTenants(updatedTenants);
      setFilteredTenants(filteredTenants.map(t =>
        t.id === editTenant.id
          ? {
              ...t,
              ...data,
              updateDate: new Date().toISOString().replace('T', ' ').substring(0, 19)
            }
          : t
      ));
      setEditTenant(null);
    } else {
      const newTenant: Tenant = {
        id: `T${10000 + tenants.length + 1}`,
        name: data.type === 'enterprise' ? data.enterpriseInfo!.name : data.schoolInfo!.name,
        status: data.status,
        type: data.type,
        createDate: new Date().toISOString().replace('T', ' ').substring(0, 19),
        adminUsername: 'new_admin',
        updateDate: new Date().toISOString().replace('T', ' ').substring(0, 19),
        creator: 'system_admin',
        remark: data.remark,
        userCount: 0,
        contact: data.contact,
        contactPhone: data.contactPhone,
        datacenter: data.datacenter,
        enterpriseInfo: data.enterpriseInfo,
        schoolInfo: data.schoolInfo,
        wallet: {
          balance: 0,
          totalRecharge: 0,
          totalConsumption: 0
        },
        points: data.type === 'school' ? {
          available: 0,
          configured: 0,
          consumed: 0,
          remaining: 0,
          totalRecharge: 0,
          totalConsumption: 0,
          totalExpired: 0
        } : undefined,
        networkInfo: {
          datacenter: data.datacenter,
          latency: 0,
          packetLoss: 0,
          bandwidth: '1000 Mbps',
          status: 'normal'
        },
        rechargeLogs: []
      };
      setTenants([...tenants, newTenant]);
      setFilteredTenants([...filteredTenants, newTenant]);
      setShowCreateModal(false);
    }
  };

  return (
    <MainLayout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">租户列表</h1>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-teal-600 text-white text-sm rounded-md hover:bg-teal-700 transition-colors whitespace-nowrap"
          >
            <i className="ri-add-line mr-2"></i>
            新建租户
          </button>
        </div>

        <SearchFilters onSearch={handleSearch} />

        <div className="mb-4 flex items-center gap-3">
          <button
            onClick={() => handleBatchAction('enable')}
            className="px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors whitespace-nowrap"
          >
            <i className="ri-check-line mr-2"></i>
            批量启用
          </button>
          <button
            onClick={() => handleBatchAction('disable')}
            className="px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 transition-colors whitespace-nowrap"
          >
            <i className="ri-close-line mr-2"></i>
            批量禁用
          </button>
          {selectedIds.length > 0 && (
            <span className="text-sm text-gray-600">已选择 {selectedIds.length} 项</span>
          )}
        </div>

        <TenantTable
          tenants={filteredTenants}
          selectedIds={selectedIds}
          onSelectAll={handleSelectAll}
          onSelectOne={handleSelectOne}
          onStatusChange={handleStatusChange}
          onView={setDetailTenant}
          onEdit={setEditTenant}
          onRemarkChange={handleRemarkChange}
        />

        {detailTenant && (
          <TenantDetailModal
            tenant={detailTenant}
            onClose={() => setDetailTenant(null)}
            onStatusChange={handleStatusChange}
          />
        )}

        {(showCreateModal || editTenant) && (
          <TenantFormModal
            tenant={editTenant}
            onClose={() => {
              setShowCreateModal(false);
              setEditTenant(null);
            }}
            onSave={handleSave}
          />
        )}

        <ConfirmModal
          show={batchAction.show}
          title={batchAction.action === 'enable' ? '确认批量启用' : '确认批量禁用'}
          message={`确定要${batchAction.action === 'enable' ? '启用' : '禁用'}选中的 ${selectedIds.length} 个租户吗？`}
          onConfirm={handleConfirmBatchAction}
          onCancel={() => setBatchAction({ show: false, action: 'disable' })}
        />
      </div>
    </MainLayout>
  );
}
